import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { API as AWS_API } from 'aws-amplify';
import { CONST_AWS_API } from 'src/app/core/constants';
import { CommanSubscribeService } from 'src/app/core/services/comman-subscribe.service';
import { environment } from 'src/environments/environment';
import { MIN_ARENA_CARDS, NetworkRecs, REC_LIMIT, RecCard, buildRecRow, recSourceIds, toRecCards } from '../video-detail-recs';

export interface RecContext {
  album: any;
  currentId: number;
  channelId: number;
  performerId: any;
  accessToken: string;
  countryCode: string;
  fallbackImage: string;
}

/** Loads the recommended row (spec US-12, US-13) and opens a recommended album. Provided per page. */
@Injectable()
export class VdRecsService {
  cards: RecCard[] = [];
  title = '';
  private run = 0;

  constructor(private router: Router, private commanSubscribe: CommanSubscribeService) {}

  /** Never throws and never shows the page loader: a row that can't load simply stays hidden. */
  async load(ctx: RecContext): Promise<void> {
    const run = ++this.run;
    this.cards = [];
    // Signed-in viewers: ask for watch-history picks at the same time as the arena / tags (no extra wait).
    const early = ctx.accessToken ? this.network(ctx) : null;
    const pages = await Promise.all(recSourceIds(ctx.album).map(id => this.post(CONST_AWS_API.GET_VOD_BY_TAG, { tag_id: id }, ctx)
      .then(res => res.Data).catch(() => [])));
    let network = early ? await early : null;
    // Guests: the network's newest only when the arena isn't a home row or has too few others.
    if (!network && toRecCards(pages, ctx.currentId, ctx.countryCode).length < MIN_ARENA_CARDS) network = await this.network(ctx);
    if (run !== this.run) return;
    const row = buildRecRow(ctx.album, pages, network, ctx.currentId, ctx.countryCode, ctx.fallbackImage);
    this.cards = row.cards;
    this.title = row.title;
  }

  private network(ctx: RecContext): Promise<NetworkRecs | null> {
    if (!ctx.album?.organization_id) return Promise.resolve(null);
    return this.post(CONST_AWS_API.GET_RECOMMENDED_ALBUMS, { organization_id: ctx.album.organization_id, exclude_id: ctx.currentId }, ctx)
      .then(res => ({ items: res.Data, personalised: res.personalised }))
      .catch(() => null);
  }

  private async post(api: string, extra: any, ctx: RecContext): Promise<{ Data: any[]; personalised: boolean }> {
    const body: any = {
      ...extra, channel_id: ctx.channelId || 0, network_group_id: this.commanSubscribe.activeGroupId || 0, limit: REC_LIMIT + 1,
    };
    if (ctx.performerId) body.performer_id = ctx.performerId;
    const res: any = await AWS_API.post(environment.AWS_Cognito.apiName, api, {
      headers: { 'Content-Type': 'application/json', 'x-api-key': environment.AWS_Cognito.X_Api_Key, Authorization: `Bearer ${ctx.accessToken || ''}` },
      response: true,
      body,
    });
    const ok = res?.data?.statusCode === '200';
    return { Data: ok ? res.data.Data || [] : [], personalised: ok && !!res.data.personalised };
  }

  /**
   * Opens a recommended album like a fresh visit. The album page reads its album once, so when only the slug
   * changes the router must not reuse it; this applies to this one navigation only.
   */
  open(card: RecCard, channelName: string, isPreview: boolean, token?: string): Promise<boolean> {
    const base = this.commanSubscribe.getChannelBasePath(channelName);
    const path = `${base}/album/${card.category}/${card.slug}${isPreview ? '/preview' : ''}`;
    this.commanSubscribe.albumBackHistory = path;
    const strategy = this.router.routeReuseStrategy;
    const reuse = strategy.shouldReuseRoute;
    strategy.shouldReuseRoute = (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot) =>
      reuse.call(strategy, future, curr) && future.params?.slug_name === curr.params?.slug_name;
    return this.router.navigate([path], isPreview && token ? { queryParams: { token } } : {})
      .finally(() => (strategy.shouldReuseRoute = reuse));
  }
}
